import { useQuery, type UseQueryOptions, type UseQueryResult } from 'react-query';
import { handleFactoryError } from './globalErrorHandler';

export interface EntityQueryConfig<TData, TParams> {
  queryKey: string[];
  queryFn: (params: TParams | undefined) => Promise<TData>;
  /** If true, suppress the global error toast for this query. */
  silent?: boolean;
  options?: Omit<UseQueryOptions<TData, unknown, TData, unknown[]>, 'queryKey' | 'queryFn'>;
}

/**
 * Factory that creates a typed useQuery wrapper.
 * Returns a hook function: (params?, overrides?) => UseQueryResult<TData>
 *
 * Automatically shows error toasts via the registered global error handler
 * unless `silent: true` is set in the config or `onError` is provided in overrides.
 */
export function createEntityQuery<TData, TParams = void>(
  config: EntityQueryConfig<TData, TParams>
) {
  return function useEntityQuery(
    params?: TParams,
    overrides?: Omit<UseQueryOptions<TData, unknown, any, unknown[]>, 'queryKey' | 'queryFn'>
  ): UseQueryResult<any> {
    const fullKey: unknown[] =
      params !== undefined ? [...config.queryKey, params] : config.queryKey;

    return useQuery(
      fullKey,
      async () => config.queryFn(params),
      {
        ...config.options,
        ...overrides,
        onError: (error: unknown) => {
          if (!config.silent && !overrides?.onError) {
            handleFactoryError(error);
          }
          config.options?.onError?.(error);
          overrides?.onError?.(error);
        },
      }
    );
  };
}
